import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from "react-intl";
import {VisitRequestDto} from "../../../../../../modules/vms/dto/VisitRequestDto";
import Descriptions from "../../../../../../components/Descriptions";
import DescriptionItem from "../../../../../../components/DescriptionItem";
import {Button, Card, CardActions, CardContent, CardHeader} from "@mui/material";
import StringUtils from "../../../../../../utils/StringUtils";
import ApproveButton from "../../../../../../components/buttons/ApproveButton";
import RejectButton from "../../../../../../components/buttons/RejectButton";
import {VisitRequestApi} from "../../../../../../modules/vms/api/VisitRequestApi";
import {HttpStatus} from "../../../../../../modules/common/enums/HttpStatus";
import {MessageUtils} from "../../../../../../utils/MessageUtils";
import LoadingBackdrop from "../../../../../../components/LoadingBackdrop";
import RejectDialog from "../../../../../workflow/flow/components/RejectDialog";
import {useConfirm} from "material-ui-confirm";

export type RequestDetailCardProps = {
    visitRequest: VisitRequestDto;
    onRefresh: () => void;
};

const RequestDetailCard: React.FC<RequestDetailCardProps> = (props) => {
    const intl = useIntl();
    const confirm = useConfirm();

    const [isLoading, setIsLoading] = useState(false);
    const [openRejectDialog, setOpenRejectDialog] = useState(false);

    const _handleApprove = () => {
        confirm({title: intl.formatMessage({id: 'WORKFLOW_PROCESS_APPROVE_CONFIRM'})})
            .then(() => _sendApproveRequest())
            .catch(() => {
                console.log('cancel');
            });
    }

    const _sendApproveRequest = async () => {
        setIsLoading(true)
        const responseResult = await VisitRequestApi.approve(props.visitRequest.id);
        if (responseResult.status === HttpStatus.SUCCESS) {
            MessageUtils.success(intl.formatMessage({id: 'WORKFLOW_PROCESS_APPROVE_SUCCESS'}));
            props.onRefresh();
        }
        setIsLoading(false);
    }

    const _handleReject = async (remarks: string) => {
        setIsLoading(true)
        setOpenRejectDialog(false);
        const responseResult = await VisitRequestApi.reject(props.visitRequest.id, remarks);
        if (responseResult.status === HttpStatus.SUCCESS) {
            MessageUtils.success(intl.formatMessage({id: 'WORKFLOW_PROCESS_REJECT_SUCCESS'}));
            props.onRefresh();
        }
        setIsLoading(false);
    }

    const visitorName = props.visitRequest.visitors.map(v => v.lastName + " " + v.firstName).join(',');
    const visitorMobile = props.visitRequest.visitors.map(v => v.mobile).join(',');
    const visitorCompany = props.visitRequest.visitors.map(v => v.company).join(',');
    const visitorEmail =  props.visitRequest.visitors.map(v => v.email).join(',');
    const visitorIdNumber = props.visitRequest.visitors.map(v => v.idNumber).join(',');
    const visitPlaces = props.visitRequest.places && props.visitRequest.places.map(p => p.areaName).join(',');
    const visitorPlateNumber = props.visitRequest.visitors.map(v => v.plateNumber).join(',');
    const visitorEmpNo = props.visitRequest.visitors.map(v => v.remarks).filter(v=>v!=='').join(',');

    const carryGoods = props.visitRequest.visitors[0].carryGoods;
    let carryGoodsJson;
    if (carryGoods) {
        carryGoodsJson = JSON.parse(carryGoods);
    }

    return (
        <Card>
            <LoadingBackdrop open={isLoading} onClick={() => setIsLoading(false)}/>
            <RejectDialog onCancel={() => setOpenRejectDialog(false)} onOk={(value) => _handleReject(value)}
                          open={openRejectDialog}/>
            <CardHeader title={props.visitRequest.number}/>
            <CardContent>
                <Descriptions column={1}>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_ARRIVE_DATE'})}>
                        {StringUtils.formatDate(props.visitRequest.arriveStartDate)}
                    </DescriptionItem>
                    {/*<DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_ARRIVE_TIME'})}>*/}
                    {/*    {StringUtils.formatTime(props.visitRequest.arriveStartTime) + "~" + StringUtils.formatTime(props.visitRequest.arriveEndTime)}*/}
                    {/*</DescriptionItem>*/}
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_HOST_NAME'})}>
                        {props.visitRequest.hostFullName}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_PURPOSE'})}>
                        {props.visitRequest.purpose}
                    </DescriptionItem>
                    {visitorEmpNo!==''&&
                        <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_EMPLOYEE_NO'})}>
                            {visitorEmpNo}
                        </DescriptionItem>}
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_NAME'})}>
                        {visitorName}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_MOBILE'})}>
                        {visitorMobile}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_EMAIL'})}>
                        {visitorEmail}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_ID_NUMBER'})}>
                        {visitorIdNumber}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_COMPANY'})}>
                        {visitorCompany}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_PLACE'})}>
                        {visitPlaces}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_REQUEST_VISITOR_PLATE_NUMBER'})}>
                        {visitorPlateNumber}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_TRANSPORT_GOODS'})}>
                        {carryGoodsJson && carryGoodsJson.transportGoods}
                    </DescriptionItem>
                    <DescriptionItem label={intl.formatMessage({id: 'VMS_VISIT_REQUEST_PERSONAL_GOODS_DESCRIPTION'})}>
                        {carryGoodsJson && carryGoodsJson.personalGoodsDescription}
                    </DescriptionItem>
                </Descriptions>
            </CardContent>
            <CardActions>
                <RejectButton onClick={() => setOpenRejectDialog(true)}/>
                <ApproveButton onClick={() => _handleApprove()}/>
            </CardActions>
        </Card>
    );
};

export default RequestDetailCard;
